import { I18N_DOMAIN } from '../../../editor/settings';
import { copyURLToClipboard, quizAlert } from '../helpers';
import { validationCheck } from '../validation-check';

const { _nx, __ } = wp.i18n;
const { addQueryArgs } = wp.url;

/**
 * Posts to the personality-answers custom endpoint.
 *
 * @param {array} selectedAnswers - Array of selected answer IDs.
 * @param {array} questionIds - Array of question IDs relevant to the answer IDs
 * @param {string} personalityId - The personality quiz unique id.
 *
 */
const sendSubmit = async (selectedAnswers, personalityId, questionIds) =>
	wp
		.apiFetch({
			path: `/mavis/v1/personality-results`,
			method: 'POST',
			data: {
				selected_answers: selectedAnswers,
				personality_id: personalityId,
				question_ids: questionIds,
			},
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Returns the question factoid.
 * @param {string} personalityId Personality post ID.
 * @param {string} questionId Question ID
 * @returns {object}
 */
const getQuestionFactoid = async (personalityId, questionId) =>
	wp
		.apiFetch({
			path: addQueryArgs('/mavis/v1/personality-question-factoid', {
				personality_id: personalityId,
				question_id: questionId,
			}),
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Checks all questions are answered.
 *
 * @param {array} personality - Array of personality quizzes on a page/post
 *
 */
const allQuestionsAnswered = (personality, clickedQuestionId) => {
	let allAnswered = true;
	const personalityQuestions = personality.querySelectorAll('.wp-block-mavis-personality-question');

	personalityQuestions.forEach((personalityQuestion) => {
		const { questionAnswered, questionId } = personalityQuestion.dataset;

		if (questionId !== clickedQuestionId && questionAnswered === '0') {
			allAnswered = false;
		}
	});

	return allAnswered;
};

/**
 * Resets the quiz so the user can play again.
 * @returns {void}
 */
const handlePlayAgain = () => {
	// Get elements we need to reset.
	const personalityForm = document.querySelector('form.wp-block-mavis-personality');
	const answerInputs = [...document.querySelectorAll('.wp-block-mavis-personality-answer input')];
	const selectedAnswerElements = [
		...document.querySelectorAll('.wp-block-mavis-personality-answer.is-selected'),
	];
	const results = document.querySelector('.mavisPersonality-results');
	const submitArea = document.querySelector('.mavisPersonality-submit-area');
	const postTitle = document.querySelector('.wp-block-post-title');
	const factoids = document.querySelectorAll('.mavisPersonality-factoid');
	const questions = document.querySelectorAll('.wp-block-mavis-personality-question');

	// Reset all inputs back to original state.
	answerInputs.forEach((input) => {
		input.checked = false; // eslint-disable-line no-param-reassign
		input.removeAttribute('disabled');
	});
	selectedAnswerElements.forEach((answer) => answer.classList.remove('is-selected'));
	submitArea.classList.remove('is-inactive');

	// Hide factoids.
	factoids.forEach((factoid) => {
		factoid.innerHTML = ''; // eslint-disable-line no-param-reassign
		factoid.classList.remove('is-active');
	});

	// Reset questions.
	questions.forEach((question) => question.setAttribute('data-question-answered', '0'));

	// Hide results and remove the output we built to show the score.
	results.classList.remove('is-active');
	results.replaceChildren();

	// Reset the form.
	personalityForm.reset();

	if (window.dataLayer) {
		// Push retake to GA.
		window.dataLayer.push({
			event: 'personalityQuizInteraction',
			interactionType: 'retake',
			quizName: document.querySelector('.wp-block-post-title').innerText,
		});
	}

	// Scroll back to top of post.
	postTitle.scrollIntoView();
};

/**
 * Checks if the amount of selected items, if all are selected remove class from button
 *
 * @param {array} selectedAnswers - Array of all the selected answers in the personality quiz
 * @param {array} questionIds - Array of question IDs relevant to the answer IDs
 * @param {string} personalityId - The unique personality quiz ID
 *
 */
const handleSubmit = async (selectedAnswers, personalityId, questionIds) => {
	const response = await sendSubmit(selectedAnswers, personalityId, questionIds);

	if (response) {
		const { personalityName, personalityDesc, personalityImage } = response;

		if (window.dataLayer) {
			// Push submit to GA.
			window.dataLayer.push({
				event: 'personalityQuizComplete',
				quizName: document.querySelector('.wp-block-post-title').innerText,
				personalityOutcome: personalityName,
			});
		}

		// Get the current personality block
		const personality = document.querySelector(`[data-question-id="${personalityId}`);

		// Get all questions for the personality quiz
		const personalityQuestions = personality.querySelectorAll(
			'.wp-block-mavis-personality-question',
		);

		// Loop over each question and if it has the class is-unanswered remove it
		personalityQuestions.forEach((element) => {
			if (element.classList.contains('is-unanswered')) {
				element.classList.remove('is-unanswered');
			}
		});

		// Find the results element
		const results = personality.querySelector('.mavisPersonality-results');

		// Update the aria-live attribute for when content is added
		results.setAttribute('aria-live', 'polite');

		// Build the output for the all the results elements
		const output = `
		<div class="mavisPersonality-resultsHeader">
			${personalityImage}
    	<span class="mavisPersonality-resultsHeader-container">
				<span class="mavisPersonality-resultsHeader-prefix">
					${__('You are:', I18N_DOMAIN)}
				</span>
				<span class="mavisPersonality-resultsHeader-name">
					${personalityName}
				</span>
			</span>
  	</div>
  	<div class="mavisPersonality-resultsContent">
    	<span class="mavisPersonality-resultsDescription">${personalityDesc}</span>
			<span class="mavisPersonality_resultsContent-question">
				<strong>
					${__('What do you want to do now?', 'mavis')}
				</strong>
			</span>
			<div class="mavisPersonality_resultsContent-buttons" role="button">
				<div class="wp-block-button">
					<button class="wp-block-button__link wp-element-button has-mavis-color-personality-secondary-background-color play-again">
						${__('Play again', 'mavis')}
					</button>
				</div>
				<div class="wp-block-button">
					<button class="wp-block-button__link wp-element-button has-mavis-color-personality-secondary-background-color share-with-friends">
						${__('Share with friends', 'mavis')}
					</button>
				</div>
  	</div>
		${quizAlert}`;

		// Insert the rest of the output elements AFTER the image
		results.insertAdjacentHTML('beforeend', output);

		// add class to display the results
		results.classList.add('is-active');

		document
			.querySelector('button.wp-block-button__link.share-with-friends')
			.addEventListener('click', (event) => {
				copyURLToClipboard(event);
				if (window.dataLayer) {
					// Push share to GA.
					window.dataLayer.push({
						event: 'personalityQuizInteraction',
						interactionType: 'share',
						quizName: document.querySelector('.wp-block-post-title').innerText,
					});
				}
			});

		document
			.querySelector('button.wp-block-button__link.play-again')
			.addEventListener('click', handlePlayAgain);
	}
};

/**
 * Checks if the amount of selected items, if all are selected remove class from button
 *
 * @param {array} personality - Array of personality quizzes on a page/post
 *
 */
const submitButtonValidation = (personality, questionId) => {
	// If the number of disabled answers is equal to the number of questions
	if (allQuestionsAnswered(personality, questionId)) {
		// Get validation warning
		const validationWarning = personality.querySelector('.mavisPersonality-validation-warning');

		// Check if it exists on the page
		if (validationWarning) {
			validationWarning.classList.remove('is-active');
			validationWarning.innerHTML = '';
		}

		// Get submit button
		const submitButton = personality.querySelector(
			'.mavisPersonality-submit-area .wp-block-button button',
		);
		// And remove the class that disables the button
		if (submitButton.classList.contains('missing-answers')) {
			submitButton.classList.remove('missing-answers');
		}
	}
};

/**
 * Handles submit click, validation warnings and sends data to handleSubmit
 *
 * @param {element} personality - the relevant personality quiz element
 * @param {object} event - event object from click event listener
 *
 */
const handleClick = (personality, event) => {
	// Get all of the questions in a personality quiz
	const personalityId = event.target.closest('.wp-block-mavis-personality').dataset.questionId;

	const questions = personality.querySelectorAll('.wp-block-mavis-personality-question');

	const questionIds = [];
	const questionNumbers = [];

	// Filter inputs for checked inputs only, and grab their ids
	const selectedOptions = [...personality.getElementsByTagName('input')]
		.filter((input) => input.checked)
		.map((input) => input.id);

	// Check if the user has answered all questions, if not output the missing ones, highlight them and return
	if (selectedOptions.length < questions.length) {
		// Check all questions for "checked" class, if not present, highlight question as unanswered
		questions.forEach((question) => {
			if (question.dataset.questionAnswered === '0') {
				question.classList.add('is-unanswered');

				questionNumbers.push(question.dataset.questionNumber);
			}
		});

		// Get the validation element for the personality quiz
		const validationWarning = personality.querySelector('.mavisPersonality-validation-warning');

		// Find the question numbers for unchecked options
		const questionTotal = questionNumbers.length;
		const finalNumber = questionNumbers.pop();
		const numberOutput = questionNumbers.length
			? `${questionNumbers.join(', ')}
			& ${finalNumber}`
			: finalNumber;

		// Update the element innerHTML to output the missing answers
		validationWarning.innerHTML = _nx(
			`You forgot to fill in Question ${numberOutput}`,
			`You forgot to fill in Questions ${numberOutput}`,
			questionTotal,
			'number of questions',
			I18N_DOMAIN,
		);

		// Add class to display the results
		validationWarning.classList.add('is-active');

		event.target.classList.add('missing-answers');

		return;
	}

	// If all questions have been answered and submitted grab submit area
	const submitArea = personality.querySelector('.mavisPersonality-submit-area');

	// and remove the submit area
	if (submitArea) {
		submitArea.classList.add('is-inactive');
	}

	// If all questions are answered, grab element
	const validationWarning = personality.querySelector('.mavisPersonality-validation-warning');

	// If element exists, remove it
	if (validationWarning) {
		validationWarning.classList.remove('is-active');
		validationWarning.innerHTML = '';
	}

	// If all questions have been answered disable all inputs until user choses to retake quiz
	const unselectedOptions = [...personality.getElementsByTagName('input')].filter(
		(input) => input.checked === false,
	);

	unselectedOptions.forEach((input) => {
		input.setAttribute('disabled', true);
	});

	// For each of the selected answers, push their ids to an array
	selectedOptions.forEach((option) => {
		questionIds.push(
			document.getElementById(option).closest('.wp-block-mavis-personality-question').dataset
				.questionId,
		);
	});

	handleSubmit(selectedOptions, personalityId, questionIds);
};

/**
 * Handles input click, and highlights the chosen options
 *
 * @param {object} event - event object from click event listener
 *
 */
const handleInput = async (event) => {
	// Get the closest question to the selected answer
	const relevantQuestion = event.target.closest('.wp-block-mavis-personality-question');
	const answer = event.target.closest('.wp-block-mavis-personality-answer').querySelector('input');

	if (window.dataLayer) {
		// Push interaction to GA
		window.dataLayer.push({
			event: `personalityQuizInteraction`,
			interactionType: `answerSelect`,
			quizName: document.querySelector('.wp-block-post-title').innerText,
			questionNumber: relevantQuestion.getAttribute('data-question-number'),
			questionText: relevantQuestion.querySelector('h2').innerText,
			answerChosenText: answer.getAttribute('title'),
			answerChosenLetter: answer.getAttribute('data-answer-letter'),
		});
	}

	// Updates question-answered data attribute
	relevantQuestion.setAttribute('data-question-answered', '1');

	// On click check if question has the unanswered class applied and if so, remove it
	if (relevantQuestion.classList.contains('is-unanswered')) {
		relevantQuestion.classList.remove('is-unanswered');
	}

	// Check if any answers have already been selected
	const previouslySelected = relevantQuestion.querySelectorAll(
		'.wp-block-mavis-personality-answer.is-selected',
	);

	// If there are selected answers, once a different answer is selected remove the is-selected
	if (previouslySelected) {
		previouslySelected.forEach((element) => {
			element.classList.remove('is-selected');
		});
	}

	// Get the factoid element for the relevant question
	const factoidEl = relevantQuestion.querySelector('.mavisPersonality-factoid');

	// When an option has been selected, add a class to reveal the factoid
	if (factoidEl && !factoidEl.innerHTML) {
		const { questionId: personalityId } = event.target.closest(
			'.wp-block-mavis-personality',
		).dataset;
		const { questionId } = relevantQuestion.dataset;
		const factoidResp = await getQuestionFactoid(personalityId, questionId);
		const { factoid } = factoidResp;
		factoidEl.innerHTML = factoid;
		factoidEl.classList.add('is-active');
	}

	// Add is-selected class to the new selected option
	event.target.parentElement.classList.add('is-selected');

	// Uncheck all other answers that are not the selected answer.
	const allAnswerEls = relevantQuestion.querySelectorAll('.wp-block-mavis-personality-answer');
	allAnswerEls.forEach((answerEl) => {
		const input = answerEl.querySelector('input');
		input.checked = answerEl.classList.contains('is-selected') || false;
	});
};

window.addEventListener('DOMContentLoaded', () => {
	// Get all personality blocks
	const personalityBlocks = document.querySelectorAll('.wp-block-mavis-personality');

	// Check personality blocks exist before continuing
	if (!personalityBlocks) {
		return;
	}

	// If personality blocks exists, loop over personality block
	personalityBlocks.forEach((personality) => {
		// Add click listener for each personality block
		personality.addEventListener('click', (event) => {
			// if click is on a personality answer input
			if (
				event.target.tagName === 'INPUT' &&
				event.target.parentElement.classList.contains('wp-block-mavis-personality-answer')
			) {
				const individualQuestion = event.target.closest('.wp-block-mavis-personality-question');

				handleInput(event);
				submitButtonValidation(personality, individualQuestion);

				// Get Input ID
				const answerId = event.target.id;

				// Get all inputs for that question
				const questionAnswered = individualQuestion.querySelectorAll(
					'.wp-block-mavis-personality-answer input',
				);

				// For each answer if the answer id doesn't match the chosen on, add disabled attribute
				questionAnswered.forEach((element) => {
					if (element.id !== answerId) {
						element.classList.add('mavisPersonality-question-answered');
					} else {
						element.classList.remove('mavisPersonality-question-answered');
					}
				});

				// If all questions are not answered, run the validation check
				if (!allQuestionsAnswered(personality)) {
					validationCheck(personality, event, 'INPUT', 'personality');
				}
			}

			// If click is on the submit button
			if (
				event.target.tagName === 'BUTTON' &&
				event.target.parentElement.parentElement.classList.contains('mavisPersonality-submit-area')
			) {
				event.preventDefault();
				handleClick(personality, event);
				submitButtonValidation(personality);
			}
		});
	});
});
